import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Checkbox,
  Paper,
  Grid,
  Tooltip,
  Badge,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import SnackBar from "../../../../components/SnackBar/SnackBar";
import BackdropComponent from "../../../Spinner/Spinner";
import {
  CustomButton,
  CustomTooltip,
} from "../../../../components/RosterConfig/RosterConfigStyle";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import classes from "../../OMTBulkEdit/Common.module.css";
import { fontStyles } from "components/Common/commonStyles";
import {
  getOMTAttendance,
  saveOMTAttendance,
} from "../../../../services/userServices";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { AttachFile } from "@mui/icons-material";
import OMTAttachments from "../OMTAttachments";
import { red } from "@mui/material/colors";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useColumnFilter } from "../useColumnFilter";
import ColumnFilterPopover from "../ColumnFilterPopover";

const Attendance = forwardRef(function Attendance(props, ref) {
  const {
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    editAccess,
    agentOracleId,
    onDirtyChange,
    noRosterIdClassName,
    isUploadMode,
    isNoRosterIdSearch,
  } = props;
  const [attendanceData, setAttendanceData] = useState([]);
  const [originalAttendanceData, setOriginalAttendanceData] = useState([]);
  const [loading, setLoader] = useState(false);
  const [order, setOrder] = useState("ASC");
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const isFirstRender = useRef(true);
  const [isDateInvalid, setIsDateInvalid] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };
  const errorHandler = (open, message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const isDirty =
    JSON.stringify(attendanceData) !== JSON.stringify(originalAttendanceData);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  useImperativeHandle(ref, () => ({
    save: () => handleSave(),
  }));

  useEffect(() => {
    let cancelled = false;

    const refreshAfterAttachment = async () => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return; // Skip first render
      }

      if (openAttachmentDialog === false) {
        try {
          await fetchAttendanceData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled)
            console.error("Failed to refresh attendance data:", error);
        }
      }
    };

    refreshAfterAttachment();

    return () => {
      cancelled = true;
    };
  }, [openAttachmentDialog]);

  useEffect(() => {
    let cancelled = false;

    const loadAttendanceData = async () => {
      if (
        (rosterId !== null && rosterId !== "") ||
        (noRosterIdClassName !== null && noRosterIdClassName !== "") ||
        (agentOracleId !== null && agentOracleId !== "" && !noRosterIdClassName)
      ) {
        if (!cancelled) {
          setAttendanceData([]);
        }

        try {
          await fetchAttendanceData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to fetch attendance data:", error);
          }
        }
      } else {
        setAttendanceData([]);
      }
    };

    loadAttendanceData();

    return () => {
      cancelled = true;
    };
  }, [rosterId, agentOracleId, noRosterIdClassName]);

  const fetchAttendanceData = async (
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleStart,
    agentOracleId,
    noRosterIdClassName,
    isNoRosterIdSearch,
  ) => {
    if (
      !rosterId &&
      !asdLeadOracleId &&
      !taskTypeId &&
      !scheduleStart &&
      !agentOracleId &&
      !noRosterIdClassName
    ) {
      return;
    }

    setLoader(true);
    try {
      const response = await getOMTAttendance(
        rosterId,
        asdLeadOracleId,
        taskTypeId,
        scheduleStart,
        agentOracleId,
        noRosterIdClassName,
        isNoRosterIdSearch,
      );

      if (response?.data?.message === "Success") {
        setAttendanceData(response.data.data);
        setOriginalAttendanceData(
          JSON.parse(JSON.stringify(response.data.data)),
        );
      } else {
        setAttendanceData([]);
        setOriginalAttendanceData([]);
      }
    } catch (error) {
      const is404 =
        error?.response?.status === 404 || error?.statusCode === 404;
      if (is404) {
        setAttendanceData([]);
        setOriginalAttendanceData([]);
        if (rosterId || noRosterIdClassName) {
          errorHandler(
            true,
            "No attendance records found for the selected filters.",
            "info",
          );
        }
      } else {
        errorHandler(true, "Error fetching attendance data.", "error");
      }
    }
    setLoader(false);
  };
  const sortByCol = (data, col, direction) =>
    [...data].sort((a, b) => {
      if (typeof a[col] === "string" && typeof b[col] === "string") {
        return direction === "ASC"
          ? a[col].toLowerCase() > b[col].toLowerCase()
            ? 1
            : -1
          : a[col].toLowerCase() < b[col].toLowerCase()
            ? 1
            : -1;
      }
      return direction === "ASC"
        ? a[col] > b[col]
          ? 1
          : -1
        : a[col] < b[col]
          ? 1
          : -1;
    });

  const sorting = (col) => {
    if (order === "ASC") {
      setAttendanceData(sortByCol(attendanceData, col, "ASC"));
      setOriginalAttendanceData(sortByCol(originalAttendanceData, col, "ASC"));
      setOrder("DSC");
    } else if (order === "DSC") {
      setAttendanceData(sortByCol(attendanceData, col, "DSC"));
      setOriginalAttendanceData(sortByCol(originalAttendanceData, col, "DSC"));
      setOrder("ASC");
    }
  };
  useEffect(() => {
    setIsDateInvalid(false);

    for (let record of attendanceData) {
      const timeIn = record.TimeIn != null ? new Date(record.TimeIn) : null;
      const timeOut = record.TimeOut != null ? new Date(record.TimeOut) : null;

      let diffMinutes = 0;
      if (timeIn !== null && timeOut !== null) {
        const diffMs = timeOut - timeIn;
        diffMinutes = Math.floor(diffMs / (1000 * 60));
      }

      if (diffMinutes < 0) {
        setIsDateInvalid(true);
        setSnack({
          open: true,
          message:
            record.EmployeeName + ": time out cannot be earlier than time in.",
          severity: "warning",
        });
        break;
      } else if (diffMinutes > 1440) {
        setIsDateInvalid(true);
        setSnack({
          open: true,
          message:
            record.EmployeeName +
            ": time out cannot be more than 24 hours after time in.",
          severity: "warning",
        });
        break;
      }
    }
  }, [attendanceData]);
  const handleDateChange = (value, rowIndex, key) => {
    let trainee = attendanceData.map((item, index) => {
      if (index === rowIndex) {
        return {
          ...item,
          [key]: value,
        };
      } else {
        return item;
      }
    });
    setAttendanceData(trainee);
  };

  const handleChangeChecked = (event, rowIndex) => {
    const { name, checked } = event.target;

    let trainee = attendanceData.map((item, index) => {
      if (index === rowIndex) {
        const updatedItem = {
          ...item,
          [name]: checked,
        };

        // reset dependent checkboxes whenever "Did not Complete" is unchecked
        if (name === "DidNotCompleted" && !checked) {
          updatedItem.TimeConstraints = false;
          updatedItem.PowerOutage = false;
          updatedItem.AgentDisconnected = false;
          updatedItem.TechDisconnected = false;
        }

        return updatedItem;
      } else {
        return item;
      }
    });

    setAttendanceData(trainee);
  };

  const handleTextValueChange = (event, rowIndex, fieldName) => {
    let trainee = attendanceData.map((item, index) => {
      if (index === rowIndex) {
        return {
          ...item,
          [fieldName]: event.target.value === "" ? null : event.target.value,
        };
      } else {
        return item;
      }
    });

    setAttendanceData(trainee);
  };

  const isReasonMissing = (record) =>
    !!record.DidNotCompleted &&
    !record.TimeConstraints &&
    !record.PowerOutage &&
    !record.AgentDisconnected &&
    !record.TechDisconnected;

  const handleSave = async () => {
    const missingReasonRecord = attendanceData.find(isReasonMissing);
    if (missingReasonRecord) {
      setSnack({
        open: true,
        message:
          missingReasonRecord.EmployeeName +
          ": please select at least one reason (Time/Staff Constraints, Power Outage, Agent Disconnected, or Tech Disconnected) since Did not Complete is checked.",
        severity: "warning",
      });
      return false;
    }
    const hasChanges =
      JSON.stringify(attendanceData) !== JSON.stringify(originalAttendanceData);

    if (!hasChanges) {
      setSnack({
        open: true,
        message: "No changes to Save.",
        severity: "info",
      });
      return true;
    }

    setLoader(true);
    try {
      const data = createData();

      if (data.length !== attendanceData.length) {
        return false;
      }

      const response = await saveOMTAttendance(
        data,
        localStorage.getItem("User"),
      );

      if (response?.message === "Success") {
        setSnack({
          open: true,
          message: "All changes have been saved Successfully.",
          severity: "success",
        });
        await fetchAttendanceData(
          rosterId,
          asdLeadOracleId,
          taskTypeId,
          scheduleType,
          agentOracleId,
          noRosterIdClassName,
          isNoRosterIdSearch,
        );
        return true;
      }

      setSnack({ open: true, message: "Failed to Save..", severity: "error" });
      return false;
    } catch {
      setSnack({
        open: true,
        message: "Error saving Attendance..",
        severity: "error",
      });
      return false;
    } finally {
      setLoader(false);
    }
  };

  const handleReset = async () => {
    fetchAttendanceData(
      rosterId,
      asdLeadOracleId,
      taskTypeId,
      scheduleType,
      agentOracleId,
      noRosterIdClassName,
      isNoRosterIdSearch,
    );
  };

  const createData = () => {
    const obj = [];
    for (let attendanceDataRecord of attendanceData) {
      obj.push({
        OMTAttendanceId: attendanceDataRecord.OMTAttendanceId
          ? attendanceDataRecord.OMTAttendanceId
          : 0,
        OMTClassDetailsId: attendanceDataRecord.OMTClassDetailsId,
        ClassRosterId: attendanceDataRecord.ClassRosterId,
        OracleId: attendanceDataRecord.OracleId,
        EmployeeName: attendanceDataRecord.EmployeeName,
        TraineeStatus: attendanceDataRecord.TraineeStatus,
        NoShow: attendanceDataRecord.NoShow
          ? attendanceDataRecord.NoShow
          : false,
        TimeIn: attendanceDataRecord.TimeIn,
        TimeOut: attendanceDataRecord.TimeOut,
        DidNotCompleted: attendanceDataRecord.DidNotCompleted
          ? attendanceDataRecord.DidNotCompleted
          : false,
        TimeConstraints: attendanceDataRecord.TimeConstraints
          ? attendanceDataRecord.TimeConstraints
          : false,
        PowerOutage: attendanceDataRecord.PowerOutage
          ? attendanceDataRecord.PowerOutage
          : false,
        ISPOutage: attendanceDataRecord.ISPOutage
          ? attendanceDataRecord.ISPOutage
          : false,
        AgentDisconnected: attendanceDataRecord.AgentDisconnected
          ? attendanceDataRecord.AgentDisconnected
          : false,
        TechDisconnected: attendanceDataRecord.TechDisconnected
          ? attendanceDataRecord.TechDisconnected
          : false,
        Comments: attendanceDataRecord.Comments,
        OMTScheduleStatusType: attendanceDataRecord.ScheduleType || "",
      });
    }
    return obj;
  };

  const cellPadding = {
    padding: "6px 8px",
  };

  const handleAttachmentOpen = (row) => {
    setOpenAttachmentDialog(true);
    setSelectedRow(row);
  };

  const traineeStatusFilter = useColumnFilter(attendanceData, "TraineeStatus");

  const FilteredAttendanceData = useMemo(() => {
    return attendanceData
      .map((row, originalIndex) => ({
        row,
        originalIndex,
      }))
      .filter(({ row }) => traineeStatusFilter.matches(row));
  }, [attendanceData, traineeStatusFilter.selectedValues]);

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box sx={{ ...fontStyles }}>
        <Paper sx={{ padding: 2 }}>
          <TableContainer className={classes.container}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                    }}
                    onClick={() =>
                      sorting(
                        isUploadMode ? "NoRosterIdClassName" : "ClassRosterId",
                      )
                    }
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={isUploadMode ? "Class Name" : "RID"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("EmployeeName")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Name"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("ScheduleType")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Type"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("ScheduleStatus")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Trainee Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <IconButton
                        onClick={traineeStatusFilter.openFilter}
                        size="small"
                        color={
                          traineeStatusFilter.selectedValues.length
                            ? "primary"
                            : "default"
                        }
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                    }}
                  >
                    <TextLabel
                      text={"No Show"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                    }}
                  >
                    <TextLabel
                      text={"Time In"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                    }}
                  >
                    <TextLabel
                      text={"Time Out"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Did not Complete"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Time/Staff Constraints"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Power Outage"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Agent Disconnected"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Tech Disconnected"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"ISP Outage"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Comments"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {FilteredAttendanceData.map(({ row, originalIndex }) => {
                  const rowKey = `${row.OracleId}-${originalIndex}`;
                  return (
                    <TableRow key={rowKey}>
                      <TableCell
                        align="center"
                        sx={{ ...fontStyles, ...cellPadding }}
                      >
                        <Grid container>
                          <Grid item xs={12}>
                            <TextLabel
                              text={
                                noRosterIdClassName
                                  ? row.NoRosterIdClassName
                                  : row.ClassRosterId
                              }
                              fontSize={14}
                              subHeading
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Tooltip title="Attach File">
                              <Badge
                                badgeContent={row?.AttachmentCount || 0}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    color: "#ffffff", // white text
                                    backgroundColor: red[700],
                                  },
                                }}
                              >
                                <IconButton
                                  onClick={() => handleAttachmentOpen(row)}
                                  size="small"
                                  disableRipple
                                  sx={{
                                    backgroundColor: "#3299ff",
                                    borderRadius: 1,
                                  }}
                                  aria-label="attach file"
                                >
                                  <AttachFile
                                    fontSize="small"
                                    sx={{ color: "#ffffff" }}
                                  />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.EmployeeName}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.ScheduleType}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.ScheduleStatus}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.TraineeStatus}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          name="NoShow"
                          checked={row.NoShow}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PlayCircleOutlinedIcon sx={{ color: "#3299FF" }} />
                          <TextField
                            size="small"
                            id="datetime-local"
                            type="datetime-local"
                            value={row.TimeIn || ""}
                            sx={{
                              backgroundColor: "#fff",
                              width: 200,
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor:
                                    row.TimeIn != null &&
                                    row.TimeIn > row.TimeOut
                                      ? "red"
                                      : "",
                                },
                              },
                            }}
                            InputLabelProps={{
                              shrink: true,
                              inputFormat: "YYYY-MM-DD : HH:MM",
                            }}
                            onChange={(e) =>
                              handleDateChange(
                                e.target.value === "" ? null : e.target.value,
                                originalIndex,
                                "TimeIn",
                              )
                            }
                            InputProps={{
                              sx: {
                                ...fontStyles,
                              },
                            }}
                            fullWidth
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <StopCircleOutlinedIcon sx={{ color: "#2aa154" }} />
                          <TextField
                            size="small"
                            id="datetime-local"
                            type="datetime-local"
                            value={row.TimeOut || ""}
                            sx={{
                              backgroundColor: "#fff",
                              width: 200,
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor:
                                    row.TimeOut != null &&
                                    row.TimeIn != null &&
                                    (new Date(row.TimeOut) -
                                      new Date(row.TimeIn)) /
                                      (1000 * 60) >
                                      1440
                                      ? "red"
                                      : "",
                                },
                              },
                            }}
                            InputLabelProps={{
                              shrink: true,
                              inputFormat: "YYYY-MM-DD : HH:MM",
                            }}
                            onChange={(e) =>
                              handleDateChange(
                                e.target.value === "" ? null : e.target.value,
                                originalIndex,
                                "TimeOut",
                              )
                            }
                            InputProps={{
                              sx: {
                                ...fontStyles,
                              },
                            }}
                            fullWidth
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          name="DidNotCompleted"
                          checked={row.DidNotCompleted}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          name="TimeConstraints"
                          disabled={
                            !row.DidNotCompleted ||
                            row.PowerOutage ||
                            row.AgentDisconnected ||
                            row.TechDisconnected
                          }
                          checked={row.TimeConstraints}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          name="PowerOutage"
                          disabled={
                            !row.DidNotCompleted ||
                            row.TimeConstraints ||
                            row.AgentDisconnected ||
                            row.TechDisconnected
                          }
                          checked={row.PowerOutage}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          name="AgentDisconnected"
                          disabled={
                            !row.DidNotCompleted ||
                            row.TimeConstraints ||
                            row.PowerOutage ||
                            row.TechDisconnected
                          }
                          checked={row.AgentDisconnected}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          name="TechDisconnected"
                          disabled={
                            !row.DidNotCompleted ||
                            row.TimeConstraints ||
                            row.AgentDisconnected ||
                            row.PowerOutage
                          }
                          checked={row.TechDisconnected}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          name="ISPOutage"
                          checked={row.ISPOutage}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell className={classes.minWidth}>
                        <CustomTooltip title={row.Comments || ""}>
                          <TextField
                            size="small"
                            multiline
                            fullWidth
                            maxRows={4}
                            value={row.Comments || ""}
                            onChange={(e) =>
                              handleTextValueChange(
                                e,
                                originalIndex,
                                "Comments",
                              )
                            }
                            InputProps={{
                              sx: {
                                ...fontStyles,
                              },
                            }}
                            helperText={`${
                              row.Comments ? row.Comments.length : 0
                            }/${100}`}
                            inputProps={{ maxLength: 100 }}
                            placeholder="Max 100 Characters"
                          />
                        </CustomTooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ColumnFilterPopover
            anchorEl={traineeStatusFilter.anchorEl}
            onClose={traineeStatusFilter.closeFilter}
            options={traineeStatusFilter.uniqueValues}
            selectedValues={traineeStatusFilter.selectedValues}
            onToggle={traineeStatusFilter.toggleValue}
            onClear={traineeStatusFilter.clearFilter}
          />
          <Box className={classes.buttonBox}>
            <CustomButton
              variant="contained"
              className={classes.buttonSave}
              size="small"
              onClick={handleSave}
              disabled={!editAccess || isDateInvalid}
            >
              Save
            </CustomButton>
            <CustomButton
              variant="outlined"
              className={classes.buttonReset}
              size="small"
              onClick={handleReset}
              disabled={!editAccess}
            >
              Reset
            </CustomButton>
          </Box>
        </Paper>
      </Box>
      <SnackBar snack={snack} snackReset={snackReset} />
      {openAttachmentDialog && (
        <OMTAttachments
          openAttachmentDialog={openAttachmentDialog}
          row={selectedRow}
          category="Attendance"
          onClose={() => setOpenAttachmentDialog(false)}
          editAccess={editAccess}
        />
      )}
    </>
  );
});
Attendance.propTypes = {
  rosterId: PropTypes.string,
  asdLeadOracleId: PropTypes.string,
  taskTypeId: PropTypes.string,
  scheduleType: PropTypes.string,
  editAccess: PropTypes.bool,
  agentOracleId: PropTypes.string,
  onDirtyChange: PropTypes.func,
};

export default Attendance;
